import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import LogoutIcon from '@mui/icons-material/Logout';
import LockResetIcon from '@mui/icons-material/LockReset';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  ProvSetFull,
  ProvAttemptFull,
  ProvWordForm,
  adminListSets,
  adminGetSet,
  adminCreateSet,
  adminUpdateSet,
  adminDeleteSet,
  adminExportSet,
  adminDeleteAttempt,
  adminGetAttempts,
  adminGradeAttempt,
  adminGetAiStatus,
  adminUploadImage,
  logoutAdmin,
  getErrorMessage,
} from '../provApi';
import ProvPinDialog from './ProvPinDialog';
import ProvImportPanel from './ProvImportPanel';
import LinkOffIcon from '@mui/icons-material/LinkOff';

const BRAND_COLOR = '#1a365d';

const adminButtonSx = {
  minHeight: 48,
  borderRadius: 2,
  textTransform: 'none' as const,
  fontWeight: 600,
  px: 2,
};

function formatProvAiLabel(model?: string, promptVersion?: string): string {
  const raw = String(model || '').trim();
  const pretty = raw === 'claude-sonnet-5'
    ? 'Sonnet 5'
    : raw.replace(/^claude-/, '').replace(/-/g, ' ');
  const version = promptVersion ? ` · prompt ${promptVersion}` : '';
  return pretty ? `Claude ${pretty}${version}` : `AI-rättning${version}`;
}

interface AdminPanelProps {
  onLogout: () => void;
  onDisconnect: () => void;
}

type AdminView = 'list' | 'edit' | 'import' | 'compare';

export default function AdminPanel({ onLogout, onDisconnect }: AdminPanelProps) {
  const [view, setView] = useState<AdminView>('list');
  const [sets, setSets] = useState<ProvSetFull[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [editingSet, setEditingSet] = useState<ProvSetFull | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editSource, setEditSource] = useState('');
  const [editType, setEditType] = useState<'standard' | 'glosor'>('standard');
  const [editRevealAfterAnswer, setEditRevealAfterAnswer] = useState(false);
  const [editQuestions, setEditQuestions] = useState<Array<{ _id?: string; prompt: string; answer: string; acceptableAnswers?: string[]; imageUrl?: string; forms?: ProvWordForm[] }>>([]);
  const [saving, setSaving] = useState(false);

  // AI grading state
  const [aiAvailable, setAiAvailable] = useState(false);
  const [aiStatusLabel, setAiStatusLabel] = useState('AI-rättning: okänd');
  const [grading, setGrading] = useState<string | null>(null); // attemptId being graded

  // Change PIN dialog state
  const [showChangePinDialog, setShowChangePinDialog] = useState(false);

  const [exportingSetId, setExportingSetId] = useState<string | null>(null);

  // Compare state
  const [compareSetId, setCompareSetId] = useState<string | null>(null);
  const [compareSet, setCompareSet] = useState<ProvSetFull | null>(null);
  const [compareAttempts, setCompareAttempts] = useState<ProvAttemptFull[]>([]);
  const [compareAttemptIdx, setCompareAttemptIdx] = useState(0);

  // Delete confirm
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteAttemptId, setDeleteAttemptId] = useState<string | null>(null);

  const fetchSets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminListSets();
      setSets(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSets();
    // Check AI availability
    adminGetAiStatus().then((s) => {
      setAiAvailable(s.available);
      setAiStatusLabel(s.available ? formatProvAiLabel(s.model, s.promptVersion) : 'AI-rättning är inte tillgänglig');
    }).catch(() => {
      setAiAvailable(false);
      setAiStatusLabel('AI-rättning är inte tillgänglig');
    });
  }, [fetchSets]);

  // =========================================================================
  // Create / Edit
  // =========================================================================

  const openCreate = () => {
    setEditingSet(null);
    setEditTitle('');
    setEditSubject('');
    setEditSource('');
    setEditType('standard');
    setEditRevealAfterAnswer(true); // Default: show answer after student answers
    setEditQuestions([{ prompt: '', answer: '' }]);
    setView('edit');
    setError(null);
  };

  const openEdit = async (setId: string) => {
    try {
      setLoading(true);
      const fullSet = await adminGetSet(setId);
      setEditingSet(fullSet);
      setEditTitle(fullSet.title);
      setEditSubject(fullSet.subject || '');
      setEditSource(fullSet.source || '');
      setEditType(fullSet.type || 'standard');
      setEditRevealAfterAnswer(fullSet.revealAfterAnswer || false);
      const setType = fullSet.type || 'standard';
      setEditQuestions(
        fullSet.questions.map((q) => {
          let forms = q.forms || [];
          let answer = q.answer || '';
          // For glosor böjning questions: if forms[0] is the auto-saved grundform, restore it to answer field
          if (setType === 'glosor' && forms.length > 0 && forms[0].label === 'grundform') {
            answer = forms[0].value || '';
            forms = forms.slice(1);
          }
          return {
            _id: q._id,
            prompt: q.prompt,
            answer,
            acceptableAnswers: q.acceptableAnswers || [],
            imageUrl: q.imageUrl,
            forms,
          };
        })
      );
      setView('edit');
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editTitle.trim()) {
      setError('Titel krävs');
      return;
    }
    const validQs = editQuestions.filter((q) => {
      const hasForms = q.forms && q.forms.length > 0;
      return q.prompt.trim() && (q.answer.trim() || hasForms);
    });
    if (validQs.length === 0) {
      setError('Minst ett ord/fråga med svar krävs');
      return;
    }

    // For glosor with forms: build answer from all forms (+ grundform if translation provided)
    const processedQs = validQs.map((q) => {
      if (editType !== 'glosor') return q;
      const hasForms = q.forms && q.forms.length > 0;
      if (!hasForms) return q; // translation-only question, keep as-is

      // If the answer/translation field is filled, treat it as the grundform (first fill-in)
      const grundform = q.answer.trim();
      const grundformEntry = grundform ? [{ label: 'grundform', value: grundform }] : [];
      const allForms = [...grundformEntry, ...(q.forms || [])];

      return {
        ...q,
        // Save forms including grundform (so student fills it in too)
        forms: allForms,
        acceptableAnswers: q.acceptableAnswers || [],
        // answer = all forms combined (used by AI grading and reveal), newline-separated for clarity
        answer: allForms.map((f) => `${f.label}: ${f.value}`).join('\n'),
      };
    });

    setSaving(true);
    setError(null);

    try {
      if (editingSet) {
        await adminUpdateSet(editingSet._id, {
          title: editTitle,
          subject: editSubject || undefined,
          source: editSource || undefined,
          type: editType,
          revealAfterAnswer: editRevealAfterAnswer,
          questions: processedQs,
        });
      } else {
        await adminCreateSet({
          title: editTitle,
          subject: editSubject || undefined,
          source: editSource || undefined,
          type: editType,
          revealAfterAnswer: editRevealAfterAnswer,
          questions: processedQs,
        });
      }
      await fetchSets();
      setView('list');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    setEditQuestions([...editQuestions, { prompt: '', answer: '', forms: [] }]);
  };

  const removeQuestion = (idx: number) => {
    setEditQuestions(editQuestions.filter((_, i) => i !== idx));
  };

  const updateQuestion = (idx: number, field: 'prompt' | 'answer' | 'imageUrl', value: string | undefined) => {
    const updated = [...editQuestions];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditQuestions(updated);
  };

  const addForm = (qIdx: number) => {
    const updated = [...editQuestions];
    updated[qIdx] = { ...updated[qIdx], forms: [...(updated[qIdx].forms || []), { label: '', value: '' }] };
    setEditQuestions(updated);
  };

  const removeForm = (qIdx: number, fIdx: number) => {
    const updated = [...editQuestions];
    updated[qIdx] = { ...updated[qIdx], forms: (updated[qIdx].forms || []).filter((_, i) => i !== fIdx) };
    setEditQuestions(updated);
  };

  const updateForm = (qIdx: number, fIdx: number, field: 'label' | 'value', value: string) => {
    const updated = [...editQuestions];
    const forms = [...(updated[qIdx].forms || [])];
    forms[fIdx] = { ...forms[fIdx], [field]: value };
    updated[qIdx] = { ...updated[qIdx], forms };
    setEditQuestions(updated);
  };

  const handleImageUpload = async (idx: number, file: File) => {
    try {
      const result = await adminUploadImage(file);
      updateQuestion(idx, 'imageUrl', result.imageUrl);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  // =========================================================================
  // Import
  // =========================================================================

  const openImport = () => {
    setView('import');
    setError(null);
  };

  const handleExport = async (set: ProvSetFull) => {
    try {
      setExportingSetId(set._id);
      setError(null);

      const exportPayload = await adminExportSet(set._id);
      const json = JSON.stringify(exportPayload, null, 2);
      const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeTitle = (set.title || 'provset')
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 50) || 'provset';

      link.href = url;
      link.download = `${safeTitle}-${set._id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setExportingSetId(null);
    }
  };

  // =========================================================================
  // Compare (view attempts vs facit)
  // =========================================================================

  const openCompare = async (setId: string) => {
    try {
      setLoading(true);
      const [fullSet, attempts] = await Promise.all([
        adminGetSet(setId),
        adminGetAttempts(setId),
      ]);
      setCompareSet(fullSet);
      // Only show completed attempts in compare view
      setCompareAttempts(attempts.filter(a => a.completedAt));
      setCompareSetId(setId);
      setCompareAttemptIdx(0);
      setView('compare');
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // =========================================================================
  // Delete
  // =========================================================================

  const handleDelete = async (id: string) => {
    try {
      await adminDeleteSet(id);
      setDeleteConfirmId(null);
      await fetchSets();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  // =========================================================================
  // Archive / Unarchive
  // =========================================================================

  const handleToggleArchive = async (set: ProvSetFull) => {
    try {
      await adminUpdateSet(set._id, { archived: !set.archived });
      await fetchSets();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  // =========================================================================
  // Logout
  // =========================================================================

  const handleLogout = async () => {
    try {
      await logoutAdmin();
    } catch {
      // Ignore
    }
    onLogout();
  };

  // =========================================================================
  // Render: List view
  // =========================================================================

  // Wrap all views so the change-PIN dialog stays rendered regardless of active view
  const changePinDialogElement = (
    <ProvPinDialog
      open={showChangePinDialog}
      onClose={() => setShowChangePinDialog(false)}
      onSuccess={() => setShowChangePinDialog(false)}
      allowChangePin
    />
  );

  if (view === 'list') {
    return (
      <Box>
        <Typography sx={{ fontWeight: 700, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
          Föräldraläge
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ ...adminButtonSx, bgcolor: BRAND_COLOR, '&:hover': { bgcolor: '#0f2744' } }}
          >
            Nytt provset
          </Button>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={openImport} sx={adminButtonSx}>
            Importera
          </Button>
          <Button variant="outlined" startIcon={<LockResetIcon />} onClick={() => setShowChangePinDialog(true)} sx={adminButtonSx}>
            Ändra PIN
          </Button>
          <Button variant="outlined" startIcon={<LogoutIcon />} onClick={handleLogout} sx={adminButtonSx}>
            Lås
          </Button>
          <Button variant="outlined" color="error" startIcon={<LinkOffIcon />} onClick={onDisconnect} sx={adminButtonSx}>
            Koppla från
          </Button>
        </Box>

        <Alert
          severity={aiAvailable ? 'info' : 'warning'}
          sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { fontWeight: 600 } }}
        >
          {aiStatusLabel}
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Hämtar provset...
            </Typography>
          </Box>
        ) : sets.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <QuizIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Inga provset ännu
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Skapa ett nytt eller importera från GPT-output
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
            {sets.map((s) => (
              <Card
                key={s._id}
                elevation={1}
                sx={{
                  borderRadius: { xs: 2, sm: 3 },
                  border: '1px solid',
                  borderColor: s.archived ? '#ffcc80' : '#e3e8ef',
                  opacity: s.archived ? 0.75 : 1,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1, pr: 1 }}>
                      <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {s.title}
                      </Typography>
                      {s.subject && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {s.subject}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {s.type === 'glosor' && (
                          <Chip
                            icon={<MenuBookIcon sx={{ fontSize: 14 }} />}
                            label="Glosor"
                            size="small"
                            sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }}
                          />
                        )}
                        {s.archived && <Chip label="Arkiverad" size="small" color="warning" />}
                        <Chip
                          label={`${s.questions.length} ${s.type === 'glosor' ? 'ord' : 'frågor'}`}
                          size="small"
                          sx={{ bgcolor: '#e8edf4', color: BRAND_COLOR, fontWeight: 500 }}
                        />
                        {(s.attemptCount ?? 0) > 0 && (
                          <Chip label={`${s.attemptCount} försök`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                    {s.type === 'glosor'
                      ? <MenuBookIcon sx={{ color: '#2E7D32', fontSize: { xs: 28, sm: 36 }, opacity: 0.7, ml: 1 }} />
                      : <QuizIcon sx={{ color: BRAND_COLOR, fontSize: { xs: 28, sm: 36 }, opacity: 0.6, ml: 1 }} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => openEdit(s._id)}
                      sx={{ ...adminButtonSx, flex: { xs: '1 1 140px', sm: '0 1 auto' }, bgcolor: BRAND_COLOR, '&:hover': { bgcolor: '#0f2744' } }}
                    >
                      Redigera
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CompareArrowsIcon />}
                      onClick={() => openCompare(s._id)}
                      sx={{ ...adminButtonSx, flex: { xs: '1 1 140px', sm: '0 1 auto' } }}
                    >
                      Jämför
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={exportingSetId === s._id ? <CircularProgress size={18} /> : <DownloadIcon />}
                      onClick={() => handleExport(s)}
                      disabled={exportingSetId === s._id}
                      sx={{ ...adminButtonSx, flex: { xs: '1 1 140px', sm: '0 1 auto' } }}
                    >
                      Exportera
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={s.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                      onClick={() => handleToggleArchive(s)}
                      sx={{ ...adminButtonSx, flex: { xs: '1 1 140px', sm: '0 1 auto' } }}
                    >
                      {s.archived ? 'Avarkivera' : 'Arkivera'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteConfirmId(s._id)}
                      sx={{ ...adminButtonSx, flex: { xs: '1 1 140px', sm: '0 1 auto' } }}
                    >
                      Ta bort
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
          <DialogTitle sx={{ fontWeight: 700 }}>Ta bort provset?</DialogTitle>
          <DialogContent>
            <Typography>
              Provset och alla tillhörande försök kommer att tas bort permanent.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setDeleteConfirmId(null)} sx={adminButtonSx}>Avbryt</Button>
            <Button
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              color="error"
              variant="contained"
              sx={adminButtonSx}
            >
              Ta bort
            </Button>
          </DialogActions>
        </Dialog>
        {changePinDialogElement}
      </Box>
    );
  }

  // =========================================================================
  // Render: Edit view
  // =========================================================================

  if (view === 'edit') {
    return (
      <Box>
        <Typography sx={{ fontWeight: 700, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
          {editingSet ? 'Redigera provset' : 'Nytt provset'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <TextField
            label="Titel *"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            fullWidth
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 52 } }}
          />
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="Ämne"
              value={editSubject}
              onChange={(e) => setEditSubject(e.target.value)}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 52 } }}
            />
            <TextField
              label="Källa (t.ex. sida 42-45)"
              value={editSource}
              onChange={(e) => setEditSource(e.target.value)}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 52 } }}
            />
          </Box>

          {/* Set type selector */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
              Typ av set
            </Typography>
            <ToggleButtonGroup
              value={editType}
              exclusive
              onChange={(_e, val) => {
                if (val) {
                  setEditType(val);
                  // Glosor sets always show answers (needed for flashcard mode)
                  if (val === 'glosor') setEditRevealAfterAnswer(true);
                }
              }}
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="standard" sx={{ ...adminButtonSx, px: 2 }}>
                Standard prov
              </ToggleButton>
              <ToggleButton value="glosor" sx={{ ...adminButtonSx, px: 2 }}>
                <MenuBookIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Glosor / Språk
              </ToggleButton>
            </ToggleButtonGroup>
            {editType === 'glosor' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Aktiverar flashcard-läge och stöd för böjningsformer. Visa svar direkt är alltid på för glosor.
              </Typography>
            )}
          </Box>
        </Box>

        {editType !== 'glosor' && (
          <FormControlLabel
            control={
              <Switch
                checked={editRevealAfterAnswer}
                onChange={(e) => setEditRevealAfterAnswer(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Visa rätt svar direkt efter varje fråga
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Eleven kan se facit efter att hen svarat (bra för övning/pluggande)
                </Typography>
              </Box>
            }
            sx={{ mb: 3, ml: 0 }}
          />
        )}

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {editType === 'glosor' ? `Glosor (${editQuestions.length})` : `Frågor (${editQuestions.length})`}
        </Typography>

        {editType === 'glosor' && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} icon={<MenuBookIcon />}>
            <Typography variant="body2">
              <strong>Ord</strong> = det ord eleven ska lära sig. <strong>Översättning/Svar</strong> = vad det betyder.
              Lägg till <strong>böjningsformer</strong> om eleven också ska kunna böja ordet (t.ex. bilar, bilarna).
            </Typography>
          </Alert>
        )}

        {editQuestions.map((q, idx) => (
          <Card key={idx} sx={{ mb: 2, borderRadius: 2, border: '1px solid #e3e8ef' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: BRAND_COLOR, mt: 1, minWidth: 24 }}
                >
                  {idx + 1}.
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label={editType === 'glosor' ? 'Ord / Basform' : 'Fråga'}
                    value={q.prompt}
                    onChange={(e) => updateQuestion(idx, 'prompt', e.target.value)}
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                  <TextField
                    label={
                      editType === 'glosor' && q.forms && q.forms.length > 0
                        ? 'Grundform (t.ex. harja) — blir ett fill-in fält'
                        : editType === 'glosor' ? 'Översättning / Svar'
                        : 'Rätt svar'
                    }
                    value={q.answer}
                    onChange={(e) => updateQuestion(idx, 'answer', e.target.value)}
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    size="small"
                    placeholder={
                      editType === 'glosor' && q.forms && q.forms.length > 0
                        ? 'Lämna tomt om grundformen inte ska övas'
                        : ''
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />

                  {/* Böjningsformer (only for glosor) */}
                  {editType === 'glosor' && (
                    <Box sx={{ pl: 1, borderLeft: '2px solid #e8edf4' }}>
                      {(q.forms || []).map((f, fIdx) => (
                        <Box key={fIdx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                          <TextField
                            label="Form (t.ex. plural obestämd)"
                            value={f.label}
                            onChange={(e) => updateForm(idx, fIdx, 'label', e.target.value)}
                            size="small"
                            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />
                          <TextField
                            label="Värde (t.ex. bilar)"
                            value={f.value}
                            onChange={(e) => updateForm(idx, fIdx, 'value', e.target.value)}
                            size="small"
                            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />
                          <IconButton size="small" color="error" onClick={() => removeForm(idx, fIdx)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => addForm(idx)}
                        sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
                      >
                        Lägg till böjningsform
                      </Button>
                    </Box>
                  )}

                  {/* Image */}
                  {q.imageUrl ? (
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Box
                        component="img"
                        src={q.imageUrl}
                        alt={`Bild fråga ${idx + 1}`}
                        sx={{ maxWidth: '100%', maxHeight: 160, borderRadius: 1.5, border: '1px solid #e0e0e0' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => updateQuestion(idx, 'imageUrl', undefined)}
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: '#fce4ec' } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ImageIcon />}
                      component="label"
                      sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                    >
                      Lägg till bild
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(idx, file);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                  )}
                </Box>
                <IconButton
                  onClick={() => removeQuestion(idx)}
                  size="small"
                  color="error"
                  disabled={editQuestions.length <= 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addQuestion}
          sx={{ ...adminButtonSx, mb: 3 }}
        >
          {editType === 'glosor' ? 'Lägg till ord' : 'Lägg till fråga'}
        </Button>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', position: 'sticky', bottom: 8, bgcolor: '#F5F7FA', py: 1 }}>
          <Button variant="outlined" onClick={() => setView('list')} sx={adminButtonSx}>
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ ...adminButtonSx, bgcolor: BRAND_COLOR, '&:hover': { bgcolor: '#0f2744' } }}
          >
            {saving ? 'Sparar...' : editingSet ? 'Spara ändringar' : 'Skapa provset'}
          </Button>
        </Box>
        {changePinDialogElement}
      </Box>
    );
  }

  // =========================================================================
  // Render: Import view
  // =========================================================================

  if (view === 'import') {
    return (
      <Box>
        <ProvImportPanel
          onCancel={() => setView('list')}
          onImported={async () => {
            await fetchSets();
            setView('list');
          }}
        />
        {changePinDialogElement}
      </Box>
    );
  }

  // =========================================================================
  // Render: Compare view (facit vs student answers)
  // =========================================================================

  if (view === 'compare' && compareSet) {
    const currentAttempt = compareAttempts[compareAttemptIdx];
    const compareIsGlosor = compareSet.type === 'glosor';

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
            Jämför: {compareSet.title}
          </Typography>
          <Button variant="outlined" onClick={() => setView('list')} sx={adminButtonSx}>
            Tillbaka
          </Button>
        </Box>

        {compareAttempts.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Inga försök har gjorts på detta provset ännu.
          </Alert>
        ) : (
          <>
            {/* Attempt selector */}
            <Tabs
              value={compareAttemptIdx}
              onChange={(_, idx) => setCompareAttemptIdx(idx)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                mb: 3,
                borderBottom: '2px solid',
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 56,
                  px: 2.5,
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-selected': {
                    bgcolor: '#f5f5f5',
                  },
                },
              }}
            >
              {compareAttempts.map((a, idx) => {
                const dateStr = new Date(a.startedAt).toLocaleString('sv-SE', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                }).toUpperCase();
                const pct = a.aiGraded ? a.summary.percentage : undefined;
                const pctColor = pct !== undefined
                  ? pct >= 70 ? '#4CAF50' : pct >= 40 ? '#FF9800' : '#f44336'
                  : undefined;

                return (
                  <Tab
                    key={a._id}
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          Försök {compareAttempts.length - idx}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                          {dateStr}
                        </Typography>
                        {pct !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: pctColor, lineHeight: 1.2 }}
                          >
                            {pct}%
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>

            {currentAttempt && (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip
                    label={currentAttempt.completedAt ? 'Avslutat' : 'Pågående'}
                    color={currentAttempt.completedAt ? 'success' : 'warning'}
                    size="small"
                  />
                  <Chip
                    label={`${currentAttempt.summary.answeredCount}/${currentAttempt.summary.totalQuestions} besvarade`}
                    size="small"
                  />
                  {currentAttempt.aiGraded && currentAttempt.summary.percentage !== undefined && (
                    <Chip
                      icon={<StarIcon sx={{ fontSize: 16 }} />}
                      label={
                        compareIsGlosor
                          ? `${currentAttempt.summary.totalScore ?? 0} av ${currentAttempt.summary.maxScore ?? 0} poäng (${currentAttempt.summary.percentage}%) — ${currentAttempt.summary.correctCount ?? 0} rätt, ${currentAttempt.summary.incorrectCount ?? 0} fel`
                          : `${currentAttempt.summary.totalScore ?? 0} av ${currentAttempt.summary.maxScore ?? 0} poäng (${currentAttempt.summary.percentage}%) — ${currentAttempt.summary.correctCount ?? 0} rätt, ${currentAttempt.summary.partialCount ?? 0} delvis, ${currentAttempt.summary.incorrectCount ?? 0} fel`
                      }
                      color={currentAttempt.summary.percentage >= 70 ? 'success' : currentAttempt.summary.percentage >= 40 ? 'warning' : 'error'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {aiAvailable && currentAttempt.completedAt && (
                    <Button
                      variant="contained"
                      startIcon={<AutoFixHighIcon />}
                      disabled={grading === currentAttempt._id}
                      onClick={async () => {
                        setGrading(currentAttempt._id);
                        setError(null);
                        try {
                          await adminGradeAttempt(currentAttempt._id);
                          // Re-fetch attempts to get updated grades
                          if (compareSetId) {
                            const attempts = await adminGetAttempts(compareSetId);
                            setCompareAttempts(attempts.filter(a => a.completedAt));
                          }
                        } catch (err) {
                          setError(getErrorMessage(err));
                        } finally {
                          setGrading(null);
                        }
                      }}
                      sx={{
                        ...adminButtonSx,
                        bgcolor: '#7C4DFF',
                        '&:hover': { bgcolor: '#651FFF' },
                      }}
                    >
                      {grading === currentAttempt._id ? 'Rättar med AI...' : currentAttempt.aiGraded ? 'Rätta igen med AI' : 'Rätta med AI'}
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1, mb: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteAttemptId(currentAttempt._id)}
                    sx={adminButtonSx}
                  >
                    Ta bort försök
                  </Button>
                </Box>

                {/* AI overall feedback */}
                {currentAttempt.aiGraded && currentAttempt.summary.aiFeedback && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {currentAttempt.summary.aiFeedback}
                    </Typography>
                  </Alert>
                )}

                {compareSet.questions
                  .sort((a, b) => a.order - b.order)
                  .map((q, idx) => {
                    const studentAnswer = currentAttempt.answers.find(
                      (a) => a.questionId === q._id
                    );
                    const hasAnswer = studentAnswer && studentAnswer.answerText.trim().length > 0;
                    const aiScore = studentAnswer?.aiScore ?? 0;
                    const aiFeedback = studentAnswer?.aiFeedback;
                    const qFormCount = Array.isArray(q.forms) && q.forms.length > 0 ? q.forms.length : 0;
                    const qMaxScore = compareIsGlosor ? (qFormCount > 1 ? qFormCount : 1) : 2;
                    const isCorrect = aiScore >= qMaxScore;
                    const isPartial = !isCorrect && aiScore > 0;

                    const scoreIcon = isCorrect
                      ? <StarIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                      : isPartial
                        ? <StarHalfIcon sx={{ color: '#FF9800', fontSize: 20 }} />
                        : aiScore === 0
                          ? <StarBorderIcon sx={{ color: '#f44336', fontSize: 20 }} />
                          : null;

                    return (
                      <Accordion key={q._id} defaultExpanded sx={{ mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography sx={{ fontWeight: 600, color: BRAND_COLOR }}>
                              {idx + 1}.
                            </Typography>
                            <Typography sx={{ fontWeight: 500, flex: 1 }}>
                              {q.prompt}
                            </Typography>
                            {scoreIcon && (
                              <Chip
                                icon={scoreIcon}
                                label={
                                  compareIsGlosor
                                    ? (qFormCount > 1 ? `${aiScore} av ${qMaxScore}` : (aiScore >= 1 ? 'Rätt (1p)' : 'Fel (0p)'))
                                    : (aiScore === 2 ? 'Rätt (2p)' : aiScore === 1 ? 'Delvis (1p)' : 'Fel (0p)')
                                }
                                size="small"
                                sx={{
                                  bgcolor: isCorrect ? '#E8F5E9' : isPartial ? '#FFF3E0' : '#FFEBEE',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {q.imageUrl && (
                              <Box sx={{ textAlign: 'center' }}>
                                <Box
                                  component="img"
                                  src={q.imageUrl}
                                  alt={`Bild fråga ${idx + 1}`}
                                  sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 1.5, border: '1px solid #e0e0e0' }}
                                />
                              </Box>
                            )}
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: hasAnswer
                                  ? isCorrect ? '#E8F5E9'
                                  : isPartial ? '#FFF8E1'
                                  : aiScore === 0 ? '#FFEBEE'
                                  : '#e8f5e9'
                                  : '#fff3e0',
                                border: '1px solid',
                                borderColor: hasAnswer
                                  ? isCorrect ? '#A5D6A7'
                                  : isPartial ? '#FFE082'
                                  : aiScore === 0 ? '#EF9A9A'
                                  : '#c8e6c9'
                                  : '#ffe0b2',
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                                Elevens svar:
                              </Typography>
                              <Typography sx={{ fontSize: '1rem' }}>
                                {hasAnswer ? studentAnswer!.answerText : '(Inget svar)'}
                              </Typography>
                              {aiFeedback && (
                                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                  AI: {aiFeedback}
                                </Typography>
                              )}
                            </Box>

                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#e3f2fd',
                                border: '1px solid',
                                borderColor: '#bbdefb',
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                                Facit:
                              </Typography>
                              <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                {q.answer}
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
              </Box>
            )}
          </>
        )}

        {/* Delete attempt confirm */}
        <Dialog open={!!deleteAttemptId} onClose={() => setDeleteAttemptId(null)}>
          <DialogTitle>Ta bort försök?</DialogTitle>
          <DialogContent>
            <Typography>
              Försöket och alla dess svar kommer att tas bort permanent.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteAttemptId(null)}>Avbryt</Button>
            <Button
              color="error"
              variant="contained"
              onClick={async () => {
                if (!deleteAttemptId || !compareSetId) return;
                try {
                  await adminDeleteAttempt(deleteAttemptId);
                  setDeleteAttemptId(null);
                  // Re-fetch attempts
                  const attempts = await adminGetAttempts(compareSetId);
                  const completed = attempts.filter(a => a.completedAt);
                  setCompareAttempts(completed);
                  // Adjust selected tab index
                  if (completed.length === 0) {
                    setCompareAttemptIdx(0);
                  } else if (compareAttemptIdx >= completed.length) {
                    setCompareAttemptIdx(completed.length - 1);
                  }
                } catch (err) {
                  setError(getErrorMessage(err));
                  setDeleteAttemptId(null);
                }
              }}
            >
              Ta bort
            </Button>
          </DialogActions>
        </Dialog>
        {changePinDialogElement}
      </Box>
    );
  }

  return null;
}
